import { Link } from 'react-router-dom'
import type { DashboardLiveBanner } from '../../types'

// LiveBanner - oxblood gradient banner that appears when one or more events
// are running today. Mirrors the prototype's `.live-banner`. The pulse dot is
// a CSS animation defined inline so we don't have to extend tailwind's
// keyframes for one banner.

interface Props {
  banner: DashboardLiveBanner
}

export function LiveBanner({ banner }: Props) {
  return (
    <div
      className="rounded-lg px-6 py-5 relative overflow-hidden text-parchment"
      style={{
        background: 'linear-gradient(135deg, #7a2329 0%, #5e1a1f 100%)',
        boxShadow: '0 10px 28px rgba(122, 35, 41, 0.30)'
      }}
    >
      <style>{`
        @keyframes nymr-live-pulse {
          0% { box-shadow: 0 0 0 0 rgba(253, 251, 246, 0.7); }
          70% { box-shadow: 0 0 0 12px rgba(253, 251, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(253, 251, 246, 0); }
        }
      `}</style>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full bg-parchment"
              style={{ animation: 'nymr-live-pulse 1.5s ease-in-out infinite' }}
              aria-hidden
            />
            <span className="text-[11px] font-semibold uppercase tracking-widest">
              Live now
            </span>
            <span
              className="px-2 py-0.5 rounded text-xs font-semibold"
              style={{
                background: banner.typeChip.colourBg,
                color: banner.typeChip.colourText
              }}
            >
              {banner.typeChip.name}
            </span>
          </div>
          <h2 className="font-display text-2xl font-bold leading-tight">
            {banner.eventName}
          </h2>
          <div className="text-sm text-parchment/80 mt-1.5 flex items-center gap-3 flex-wrap">
            <span>
              Day {banner.dayOfRun} of {banner.totalDays}
            </span>
            {banner.nextServiceTimeText && (
              <>
                <span aria-hidden>·</span>
                <span>
                  Next service{' '}
                  <span className="font-mono font-semibold">{banner.nextServiceTimeText}</span>
                </span>
              </>
            )}
          </div>
        </div>
        <Link
          to={`/events/${banner.eventId}`}
          className="inline-flex items-center justify-center px-4 py-2 rounded font-semibold text-sm whitespace-nowrap transition-colors"
          style={{ background: 'rgba(253, 251, 246, 0.16)' }}
        >
          Open event →
        </Link>
      </div>
    </div>
  )
}
